/**
 * Pure helpers for the reservation self-service manage flow
 * (see {@link ReservationStatusPage}). Extracted so the status/date/time
 * derivation and the mutating logic (reschedule target construction,
 * error-code mapping, guarantee gating) are unit-testable without rendering
 * the component.
 *
 * IMPORTANT — the `/api/get-reservation-data` response shape:
 * The endpoint returns `status` (string), `resDate`/`resTime` (objects with a
 * `.date` string) and does NOT return `restaurant`, `date`, `time`, or a
 * boolean `cancelled` — even though `ReservationDataResponse` still declares
 * some of those. These helpers read only the fields the endpoint actually
 * sends; the restaurant comes from the tenant context, not the reservation.
 */
import type { ReservationDataResponse } from '../api/reservations';

export type StatusView =
  | 'cancelled'
  | 'voided'
  | 'waiting'
  | 'table_ready'
  | 'confirmed'
  | 'manage';

/**
 * Map a reservation to the status view the page renders. `status` (string) is
 * the source of truth — the boolean `cancelled` field is absent on this
 * endpoint, so keying off it (the original bug) misclassified cancelled
 * reservations as "waiting".
 */
export function resolveStatusView(data: ReservationDataResponse): StatusView {
  if (data.status === 'cancelled' || data.cancelled) return 'cancelled';
  if (data.status === 'voided') return 'voided';
  if (data.confirmStatus || data.status === 'confirmed') return 'confirmed';
  if (data.fpStatus === 'table_ready') return 'table_ready';
  if (data.walkIn && data.fpStatus === 'arrived') return 'waiting';
  // A future booked reservation (deep link) → self-service manage experience.
  if (data.status === 'booked' && !data.walkIn) return 'manage';
  return 'waiting';
}

/**
 * Extract the YYYY-MM-DD portion from the reservation date (`resDate.date`,
 * "YYYY-MM-DD HH:MM:SS"). Returns null when absent/unparseable.
 */
export function reservationDateYmd(
  data: ReservationDataResponse,
): string | null {
  const raw = data.resDate?.date;
  if (!raw) return null;
  const ymd = raw.split(' ')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
}

/**
 * Extract the HH:MM the slot list uses, to highlight the current slot. The
 * booked time lives in `resTime` ("1970-01-01 HH:MM:SS"); `resDate`'s time
 * portion is 00:00:00, so resTime MUST be preferred (using resDate here was a
 * bug that highlighted the wrong/no slot).
 */
export function reservationTimeHm(
  data: ReservationDataResponse,
): string | null {
  const raw = data.resTime?.date ?? data.resDate?.date;
  if (!raw) return null;
  const parts = raw.split(' ');
  const timePart = parts.length > 1 ? parts[1] : parts[0];
  const [h, m] = timePart.split(':');
  if (h === undefined || m === undefined) return null;
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
}

/**
 * Display-format the reservation date from `resDate.date` ("YYYY-MM-DD ...")
 * as a localized "Wed, 1 Jul" string. TZ-stable (parses the date part only).
 */
export function formatReservationDate(resDate: string): string {
  try {
    const datePart = resDate.split(' ')[0];
    const date = new Date(datePart + 'T00:00:00');
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return resDate;
  }
}

/**
 * Display-format the reservation time from `resTime.date`
 * ("1970-01-01 HH:MM:SS") as "HH:MM".
 */
export function formatReservationTime(resTime: string): string {
  try {
    const parts = resTime.split(' ');
    const timePart = parts.length > 1 ? parts[1] : parts[0];
    const [h, m] = timePart.split(':');
    return `${h}:${m}`;
  } catch {
    return resTime;
  }
}

/** Guarantee states for which the backend blocks self-service modify. */
const GUARANTEED_STATES = new Set(['paid', 'pending_payment', 'requested']);

/**
 * Whether a reservation's guarantee status blocks self-service modify.
 * The backend rejects modify for these states; the UI hides the modify
 * controls when this is true.
 */
export function isGuaranteed(guaranteeStatus?: string): boolean {
  const status = guaranteeStatus?.trim();
  return !!status && GUARANTEED_STATES.has(status);
}

/**
 * Build the `newResTime` value for a same-day reschedule.
 *
 * `resDateRaw` is the reservation's stored date string (`resDate.date`,
 * "YYYY-MM-DD HH:MM:SS"). We take only its YYYY-MM-DD portion and combine
 * it with the chosen `hour` ("HH:mm") to produce "YYYY-MM-DD HH:mm".
 *
 * The date always comes from the reservation, never from the slot — the
 * backend only supports same-day reschedule and 422s on a date change, so
 * a slot from a different day can never produce a cross-date value here.
 * Returns null when the reservation date can't be parsed (the caller then
 * skips the reschedule).
 */
export function buildRescheduleDateTime(
  resDateRaw: string,
  hour: string,
): string | null {
  const ymd = resDateRaw.split(' ')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return `${ymd} ${hour}`;
}

/**
 * Map a backend `serverCode` (the machine-readable `status` on a 4xx
 * reservation-action response) to its i18n message key. Unknown or absent
 * codes fall back to the generic error key.
 */
export function messageKeyForServerCode(serverCode?: string): string {
  switch (serverCode) {
    case 'no_availability':
      return 'RESERVATION_STATUS_MODIFY_ERROR_NO_AVAILABILITY';
    case 'past_cutoff':
      return 'RESERVATION_STATUS_MODIFY_ERROR_PAST_CUTOFF';
    case 'guaranteed_modify_disabled':
      return 'RESERVATION_STATUS_MODIFY_ERROR_GUARANTEED';
    case 'not_modifiable':
      return 'RESERVATION_STATUS_MODIFY_ERROR_NOT_MODIFIABLE';
    case 'invalid_input':
      return 'RESERVATION_STATUS_MODIFY_ERROR_INVALID_INPUT';
    case 'date_change_unsupported':
      return 'RESERVATION_STATUS_MODIFY_ERROR_DATE_UNSUPPORTED';
    default:
      return 'RESERVATION_STATUS_ERROR';
  }
}
